<script lang="ts" setup>
const { t } = useI18n()
const localePath = useLocalePath()

const features = [
  {
    icon: getIcon('pricing'),
    key: 'pricing',
    to: localePath({ hash: '#pricing' }),
  },
  {
    companiesUsing: [
      'crisp.chat',
      'varos.com',
      'trustedsite.com',
    ],
    companiesUsingCount: 100,
    icon: getIcon('datapoints'),
    key: 'onDemandEnrichment',
    to: localePath({ name: 'api' }),
  },
  {
    companiesUsing: [
      'perigon.com',
      'playmakerml.com',
      'raylu.ai',
    ],
    companiesUsingCount: 30,
    icon: getIcon('ai'),
    key: 'context',
    to: localePath({
      name: 'api-slug',
      params: { slug: 'fetch-company-context' },
    }),
  },
  {
    companiesUsing: [
      'paloaltonetworks.com',
      'scribe-mail.com',
      'forestreet.com',
    ],
    companiesUsingCount: 1000,
    icon: getIcon('company'),
    key: 'bestSchema',
    to: localePath({
      name: 'api-slug',
      params: { slug: 'datapoints' },
    }),
  },
  {
    companiesUsing: [
      'tagvenue.com',
      'meilleursjobs.com',
      'demandos.com',
    ],
    companiesUsingCount: 10,
    icon: getIcon('search'),
    key: 'searchEngine',
    to: localePath({
      name: 'api-slug',
      params: { slug: 'search-companies' },
    }),
  },
  {
    companiesUsing: [
      'trustlayer.io',
      'loopcv.pro',
      'happyscribe.com',
    ],
    companiesUsingCount: 15,
    icon: getIcon('developer'),
    key: 'builtForYourProduct',
    to: localePath({ name: 'api-slug' }),
  },
]
</script>

<template>
  <LayoutSection :has-padding="false">
    <template #left>
      <SectionHeading
        class="mb-4"
        :description="t('description')"
        :title="t('title')"
      />
    </template>

    <template #right>
      <div class="-mx-4 flex flex-wrap">
        <div
          v-for="feature in features"
          :key="feature.key"
          class="mb-16 flex w-full px-4 md:w-1/2 lg:w-1/3"
        >
          <div class="mx-auto flex size-full max-w-sm flex-col items-center text-center text-base leading-8 text-gray-600 dark:text-gray-300">
            <h3 class="mb-4 flex items-center gap-4 text-pretty font-semibold leading-tight text-gray-900 md:text-lg lg:text-xl dark:text-white">
              <UIcon
                class="text-primary inline-block text-4xl"
                :name="feature.icon"
              />
              {{ t(`features.${feature.key}.title`) }}
            </h3>

            <p class="mb-4 flex-1 text-pretty text-sm md:text-base lg:text-lg">
              {{ t(`features.${feature.key}.description`) }}
            </p>

            <div class="mt-auto flex flex-col items-center pt-4">
              <p
                v-if="feature.companiesUsingCount || feature.key === 'pricing'"
                class="flex items-center gap-1.5"
              >
                <UAvatarGroup
                  v-if="feature.companiesUsing && feature.companiesUsing.length"
                  :max="3"
                  size="2xs"
                >
                  <UAvatar
                    v-for="company in feature.companiesUsing"
                    :key="company"
                    size="xs"
                    :src="getLogo(company, 'companies')"
                    :ui="{
                      rounded: 'rounded-full',
                    }"
                  />
                </UAvatarGroup>

                <span v-if="feature.key === 'pricing'">
                  <BaseCoin
                    :mono="false"
                    size="sm"
                  >
                    <span class="text-sm text-gray-500 dark:text-gray-400">
                      {{ t('creditsPerCompany', { cents: creditsToCents(1) }) }}
                    </span>
                  </BaseCoin>
                </span>

                <span
                  v-else
                  class="text-sm text-gray-500 dark:text-gray-400"
                >
                  {{ t('companiesUsing', { count: feature.companiesUsingCount }) }}
                </span>
              </p>

              <UButton
                class="mt-4"
                :label="t(`features.${feature.key}.link`)"
                :to="feature.to"
                :trailing-icon="getIcon('arrowRight')"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </LayoutSection>
</template>

<i18n lang="json">
{
  "en": {
    "companiesUsing": "{count}+ companies use this feature",
    "creditsPerCompany": "1 credit ({cents}$) per company",
    "description": "Discover why our customers choose us as their company data provider",
    "features": {
      "bestSchema": {
        "description": "Our company schema has been refined over the years to deliver the most accurate and consistent data possible. We listen to our users' needs to improve it every month. Reach out to our support if you need a new datapoint.",
        "link": "Discover all available datapoints",
        "title": "The best company schema"
      },
      "builtForYourProduct": {
        "description": "Our entire API is designed to be used in your product if you are building anything that requires company data, classification, or enrichment. You can use our API to enrich payment receipts, CRM experiences, notifications, and more.",
        "link": "Start building your own product with our API",
        "title": "Built for your product"
      },
      "context": {
        "description": "Our API not only exposes JSON data but also provides direct access to the complete context we extract for each company. Bring the best LLM experiences to your users without the hassle of crawling and indexing.",
        "link": "Start supercharging your LLM workflows",
        "title": "Ready for AI scenarios"
      },
      "onDemandEnrichment": {
        "description": "We can re-analyze any company to ensure you receive the most up-to-date profile. Data points will be refreshed using information from the company's website, social networks, search engines, and public sources.",
        "link": "Enrich your first company",
        "title": "On-demand enrichment"
      },
      "pricing": {
        "description": "We built our own infrastructure to deliver the best pricing on the market. We are not a reseller; we are the provider for resellers. We can guarantee that building and hosting your own infrastructure will cost more.",
        "link": "Discover our friendly pricing",
        "title": "The best pricing on the market"
      },
      "searchEngine": {
        "description": "Our search engine API lets you filter through 50M+ companies with 300+ datapoints. You can also filter using natural language queries and even convert them into a list of criteria with a single API call. We also remove all offline companies from the results.",
        "link": "Build your company search engine",
        "title": "The best company search API"
      }
    },
    "title": "Why use our API?"
  },
  "es": {
    "companiesUsing": "{count}+ empresas usan esta función",
    "creditsPerCompany": "1 crédito ({cents}$) por empresa",
    "description": "Descubre por qué nuestros clientes nos eligen como proveedor de datos de empresas",
    "features": {
      "bestSchema": {
        "description": "Nuestro esquema de empresa ha sido refinado a lo largo de los años para ofrecer los datos más precisos y consistentes posibles. Escuchamos las necesidades de nuestros usuarios para mejorarlo cada mes. Ponte en contacto con nuestro soporte si necesitas un nuevo punto de datos.",
        "link": "Descubre todos los puntos de datos disponibles",
        "title": "El mejor esquema de empresa"
      },
      "builtForYourProduct": {
        "description": "Toda nuestra API está construida para ser utilizada en tu producto, si estás construyendo algo que requiere datos de empresas, clasificación y enriquecimiento. Puedes usar nuestra API para enriquecer recibos de pago, experiencias de CRM, notificaciones y más.",
        "link": "Comienza a construir tu propio producto con nuestra API",
        "title": "Construida para tu producto"
      },
      "context": {
        "description": "Nuestra API no solo expone datos JSON, sino que también proporciona acceso directo al contexto completo, que extraemos para cada empresa. Lleva las mejores experiencias de LLM a tus usuarios sin la molestia de rastrear e indexar.",
        "link": "Comienza a potenciar tus flujos de trabajo de LLM",
        "title": "Lista para escenarios de IA"
      },
      "onDemandEnrichment": {
        "description": "Podemos volver a analizar cualquier empresa para garantizar que reciba el perfil más actualizado. Los puntos de datos se actualizarán utilizando información del sitio web de la empresa, redes sociales, motores de búsqueda y fuentes públicas.",
        "link": "Enriquecer tu primera empresa",
        "title": "Enriquecimiento en demanda"
      },
      "pricing": {
        "description": "Construimos nuestra propia infraestructura para ofrecer el mejor precio del mercado. No somos un revendedor, somos el proveedor de los revendedores. Podemos garantizar que construir y alojar tu propia infraestructura costará más que usar nuestra API.",
        "link": "Descubre nuestro precio amigable",
        "title": "El mejor precio del mercado"
      },
      "searchEngine": {
        "description": "Nuestra API de motor de búsqueda te permite filtrar a través de más de 50M de empresas con más de 300 puntos de datos. También puedes filtrar usando consultas en lenguaje natural e incluso convertirlas en una lista de criterios con una sola llamada a la API. También eliminamos todas las empresas offline de los resultados.",
        "link": "Construye tu motor de búsqueda de empresas",
        "title": "La mejor API de búsqueda de empresas"
      }
    },
    "title": "¿Por qué usar nuestra API?"
  },
  "fr": {
    "companiesUsing": "{count}+ entreprises utilisent cette fonctionnalité",
    "creditsPerCompany": "1 crédit ({cents}$) par entreprise",
    "description": "Découvrez pourquoi nos clients nous choisissent comme fournisseur de données d'entreprises",
    "features": {
      "bestSchema": {
        "description": "Notre schéma d'entreprise a été affiné au fil des années pour offrir les données les plus précises et cohérentes possibles. Nous écoutons les besoins de nos utilisateurs pour l'améliorer chaque mois. Contactez notre support si vous avez besoin d'un nouveau point de données.",
        "link": "Découvrez tous les points de données disponibles",
        "title": "Le meilleur schéma d'entreprise"
      },
      "builtForYourProduct": {
        "description": "Toute notre API est conçue pour être utilisée dans votre produit, si vous développez quelque chose qui nécessite des données d'entreprise, de la classification ou de l'enrichissement. Vous pouvez utiliser notre API pour enrichir les reçus de paiement, les expériences CRM, les notifications et bien plus encore.",
        "link": "Commencez à construire votre propre produit avec notre API",
        "title": "Conçue pour votre produit"
      },
      "context": {
        "description": "Notre API n'expose pas seulement des données JSON, mais fournit également un accès direct au contexte complet que nous extrayons pour chaque entreprise. Offrez les meilleures expériences LLM à vos utilisateurs sans les tracas du crawling et de l'indexation.",
        "link": "Commencez à booster vos workflows LLM",
        "title": "Prête pour les scénarios IA"
      },
      "onDemandEnrichment": {
        "description": "Nous pouvons réanalyser n'importe quelle entreprise pour vous assurer de recevoir le profil le plus à jour. Les points de données seront actualisés à partir d'informations provenant du site web de l'entreprise, des réseaux sociaux, des moteurs de recherche et de sources publiques.",
        "link": "Enrichissez votre première entreprise",
        "title": "Enrichissement à la demande"
      },
      "pricing": {
        "description": "Nous avons construit notre propre infrastructure pour offrir le meilleur prix du marché. Nous ne sommes pas un revendeur, nous sommes le fournisseur des revendeurs. Nous pouvons garantir que construire et héberger votre propre infrastructure coûtera plus cher que d'utiliser notre API.",
        "link": "Découvrez notre tarification avantageuse",
        "title": "Le meilleur prix du marché"
      },
      "searchEngine": {
        "description": "Notre API de moteur de recherche vous permet de filtrer parmi plus de 50M d'entreprises avec plus de 300 points de données. Vous pouvez également filtrer en utilisant des requêtes en langage naturel et même les convertir en une liste de critères avec un seul appel API. Nous supprimons également toutes les entreprises hors ligne des résultats.",
        "link": "Construisez votre moteur de recherche d'entreprises",
        "title": "La meilleure API de recherche d'entreprise"
      }
    },
    "title": "Pourquoi utiliser notre API ?"
  }
}
</i18n>
